import type { IFarm } from 'interfaces/IFarm';
import type { IPaddock } from 'interfaces/IPaddock';
import { sortFarmsByName, sortPaddocksByName } from 'utils/FarmUtils';

// Which farms and paddocks the Paddocks grid shows, and in what order
// (OP-2481). Pure, so the search, filter and sort rules are tested on their own.

export type PaddockStatusFilter = 'all' | 'included' | 'excluded';
export type PaddockSortKey = 'name' | 'area' | 'arableArea';
export type SortDirection = 'asc' | 'desc';

export interface PaddockGridSort {
  key: PaddockSortKey;
  direction: SortDirection;
}

export interface PaddockGridFarm {
  farm: IFarm;
  paddocks: IPaddock[];
}

interface PaddockGridOptions {
  search: string;
  status: PaddockStatusFilter;
  sort: PaddockGridSort;
}

const matchesStatus = (isExcluded: boolean, status: PaddockStatusFilter) =>
  status === 'all' || (status === 'excluded') === isExcluded;

const byNumber =
  <T>(value: (item: T) => number | null) =>
  (a: T, b: T) =>
    (value(a) ?? 0) - (value(b) ?? 0);

const sortPaddocks = (paddocks: IPaddock[], sort: PaddockGridSort) => {
  const sorted =
    sort.key === 'name'
      ? sortPaddocksByName(paddocks)
      : [...paddocks].sort(
          byNumber<IPaddock>((p) =>
            sort.key === 'area' ? p.paddockArea : p.arableArea
          )
        );
  return sort.direction === 'desc' ? sorted.reverse() : sorted;
};

// Farms have no arable area, so that sort keeps them in name order.
const sortFarms = (farms: IFarm[], sort: PaddockGridSort) => {
  if (sort.key === 'area') {
    const sorted = [...farms].sort(byNumber<IFarm>((f) => f.farmArea));
    return sort.direction === 'desc' ? sorted.reverse() : sorted;
  }
  const sorted = sortFarmsByName(farms);
  return sort.key === 'name' && sort.direction === 'desc'
    ? sorted.reverse()
    : sorted;
};

/**
 * The grid's rows. Search matches farm and paddock names (case-insensitive):
 * a matching farm shows all its paddocks, otherwise only matching paddocks
 * show, under their farm. The status filter applies to each row on its own.
 * A farm shows when it matches both, or when any of its paddocks show.
 * Deleted (inactive) paddocks never show.
 */
export const buildPaddockGridRows = (
  farms: IFarm[],
  { search, status, sort }: PaddockGridOptions
): PaddockGridFarm[] => {
  const term = search.trim().toLowerCase();
  const matches = (name: string) => !term || name.toLowerCase().includes(term);

  return sortFarms(farms, sort).flatMap((farm) => {
    const farmMatchesSearch = matches(farm.farmName);
    const paddocks = farm.paddocks.filter(
      (paddock) =>
        paddock.isActive &&
        matchesStatus(paddock.isExcluded, status) &&
        (farmMatchesSearch || matches(paddock.paddockName))
    );
    const farmShows =
      (farmMatchesSearch && matchesStatus(farm.isExcluded, status)) ||
      paddocks.length > 0;
    return farmShows ? [{ farm, paddocks: sortPaddocks(paddocks, sort) }] : [];
  });
};
